import React, { useEffect, useState } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { AppData } from '../../types';
import { useClaudeAPI } from '../../hooks/useClaudeAPI';
import { useSpeechRecognition } from '../../hooks/useSpeechRecognition';
import { Search, Sparkles, Mic, MicOff, Copy, Check, Download } from 'lucide-react';
import { generateLawyerPackage } from '../../utils/export';
interface LawyerPrepTabProps {
  data: AppData;
  onUpdateIntake: (answers: any) => void;
}
export function LawyerPrepTab({
  data,
  onUpdateIntake
}: LawyerPrepTabProps) {
  const {
    generateCaseSummary,
    findAttorneys,
    isLoading,
    error
  } = useClaudeAPI();
  const {
    isListening,
    transcript,
    startListening,
    stopListening,
    resetTranscript
  } = useSpeechRecognition();
  const [location, setLocation] = useState('');
  const [caseType, setCaseType] = useState('Employment Law');
  const [attorneys, setAttorneys] = useState<any[]>([]);
  const [summary, setSummary] = useState('');
  const [activeField, setActiveField] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const handleFindAttorneys = async () => {
    if (!location) return;
    const results = await findAttorneys(location, caseType);
    setAttorneys(results);
  };
  const handleGenerateSummary = async () => {
    const result = await generateCaseSummary(data);
    setSummary(result);
  };
  const handleCopySummary = () => {
    navigator.clipboard.writeText(summary);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const handleVoiceInput = (field: string) => {
    if (isListening && activeField === field) {
      stopListening();
      // Append transcript to existing value
      const currentValue = (data.intakeAnswers as any)[field] || '';
      onUpdateIntake({
        [field]: currentValue + (currentValue ? ' ' : '') + transcript
      });
      resetTranscript();
      setActiveField(null);
    } else {
      setActiveField(field);
      resetTranscript();
      startListening();
    }
  };
  // Update field with transcript while listening
  useEffect(() => {
    if (isListening && activeField && transcript) {
      // We don't update state here to avoid loop, just let the user see it when they stop
    }
  }, [transcript, isListening, activeField]);
  const intakeQuestions = [{
    key: 'outcome',
    label: 'What outcome are you seeking?'
  }, {
    key: 'timeline',
    label: 'When did the issues begin? Are they ongoing?'
  }, {
    key: 'context',
    label: 'What is the context/background of your situation?'
  }, {
    key: 'reporting',
    label: 'Have you reported these issues to anyone? If so, what happened?'
  }, {
    key: 'impact',
    label: 'How has this situation affected you personally?'
  }, {
    key: 'witnesses',
    label: 'Do you have any witnesses or supporting documentation?'
  }, {
    key: 'additional',
    label: 'Additional information or concerns'
  }];
  return <div className="space-y-8 max-w-4xl mx-auto" data-id="element-222">
      {/* Section 1: Find Attorneys */}
      <section data-id="element-223">
        <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-224">
          1. Find Attorneys
        </h2>
        <Card className="border-t-4 border-t-purple-500" data-id="element-225">
          <div className="grid md:grid-cols-2 gap-4 mb-4" data-id="element-226">
            <Input label="Your Location" placeholder="City, State" value={location} onChange={e => setLocation(e.target.value)} data-id="element-227" />
            <div data-id="element-228">
              <label className="block text-sm font-medium text-text-primary mb-1.5" data-id="element-229">
                Case Type
              </label>
              <select className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-purple" value={caseType} onChange={e => setCaseType(e.target.value)} data-id="element-230">
                <option data-id="element-231">Employment Law</option>
                <option data-id="element-232">Personal Injury</option>
                <option data-id="element-233">Civil Rights Violations</option>
                <option data-id="element-234">Workplace Safety/OSHA</option>
                <option data-id="element-235">Medical Malpractice</option>
                <option data-id="element-236">Housing/Tenant Rights</option>
              </select>
            </div>
          </div>
          <Button onClick={handleFindAttorneys} isLoading={isLoading && attorneys.length === 0} leftIcon={<Search className="w-4 h-4" data-id="element-238" />} className="w-full md:w-auto" data-id="element-237">
            Find Attorneys
          </Button>

          {attorneys.length > 0 && <div className="mt-6 space-y-4 animate-in fade-in slide-in-from-top-4" data-id="element-239">
              <h3 className="font-bold text-gray-900" data-id="element-240">Recommended Attorneys</h3>
              <div className="grid gap-3" data-id="element-241">
                {attorneys.map((at, idx) => <div key={idx} className="bg-gray-50 p-4 rounded-lg border border-gray-100" data-id="element-242">
                    <div className="flex justify-between items-start" data-id="element-243">
                      <div data-id="element-244">
                        <p className="font-bold text-primary-purple" data-id="element-245">
                          {at.name}
                        </p>
                        <p className="text-sm text-gray-600" data-id="element-246">{at.specialty}</p>
                        <p className="text-sm text-gray-500 mt-1" data-id="element-247">{at.phone}</p>
                      </div>
                      <Badge variant="blue" data-id="element-248">Match</Badge>
                    </div>
                    <p className="text-xs text-gray-500 mt-2 italic" data-id="element-249">
                      "{at.reason}"
                    </p>
                  </div>)}
              </div>
              <p className="text-xs text-gray-400 mt-2" data-id="element-250">
                Disclaimer: AI-generated suggestions. Verify independently.
              </p>
            </div>}
        </Card>
      </section>

      {/* Section 2: AI Summary */}
      <section data-id="element-251">
        <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-252">
          2. AI Case Summary
        </h2>
        <Card className="border-t-4 border-t-blue-500" data-id="element-253">
          <div className="flex items-start gap-4 mb-4" data-id="element-254">
            <div className="bg-blue-100 p-3 rounded-full" data-id="element-255">
              <Sparkles className="w-6 h-6 text-blue-600" data-id="element-256" />
            </div>
            <div data-id="element-257">
              <h3 className="font-bold text-gray-900" data-id="element-258">
                Generate Professional Summary
              </h3>
              <p className="text-sm text-gray-600" data-id="element-259">
                Create a concise summary of your incidents, evidence, and impact
                to share with attorneys.
              </p>
            </div>
          </div>

          {!summary ? <Button onClick={handleGenerateSummary} isLoading={isLoading && !summary} className="w-full" data-id="element-260">
              Generate Summary
            </Button> : <div className="animate-in fade-in" data-id="element-261">
              <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 font-mono text-sm whitespace-pre-wrap mb-4" data-id="element-262">
                {summary}
              </div>
              <Button variant="secondary" onClick={handleCopySummary} leftIcon={copied ? <Check className="w-4 h-4" data-id="element-264" /> : <Copy className="w-4 h-4" data-id="element-265" />} data-id="element-263">
                {copied ? 'Copied!' : 'Copy to Clipboard'}
              </Button>
            </div>}
        </Card>
      </section>

      {/* Section 3: Intake Questions */}
      <section data-id="element-266">
        <div className="flex justify-between items-center mb-4" data-id="element-267">
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-268">
            3. Intake Preparation
          </h2>
          <Button variant="outline" className="text-primary-purple border-primary-purple hover:bg-purple-50" onClick={() => generateLawyerPackage(data)} leftIcon={<Download className="w-4 h-4" data-id="element-270" />} data-id="element-269">
            Export Package
          </Button>
        </div>

        <div className="space-y-4" data-id="element-271">
          {intakeQuestions.map(q => <Card key={q.key} className="p-4" data-id="element-272">
              <label className="block font-bold text-gray-900 mb-2" data-id="element-273">
                {q.label}
              </label>
              <div className="relative" data-id="element-274">
                <Input multiline value={(data.intakeAnswers as any)[q.key] + (activeField === q.key ? transcript ? ' ' + transcript : '' : '')} onChange={e => onUpdateIntake({
              [q.key]: e.target.value
            })} className="min-h-[100px] pb-10" data-id="element-275" />
                <button onClick={() => handleVoiceInput(q.key)} className={`
                    absolute bottom-2 right-2 p-2 rounded-full transition-colors
                    ${activeField === q.key && isListening ? 'bg-red-100 text-red-600 animate-pulse' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}
                  `} title="Voice Input" data-id="element-276">
                  {activeField === q.key && isListening ? <MicOff className="w-4 h-4" data-id="element-277" /> : <Mic className="w-4 h-4" data-id="element-278" />}
                </button>
              </div>
            </Card>)}
        </div>
      </section>
    </div>;
}